import { ControlledAutocompleteInput, ControlledInput, Indent, InputMasks, Stack } from '@ui-components/neo.tax';

import { AccountingQre } from '@neo.tax/rd/hooks/useAccountingQres';
import {
  COUNTRY_OPTIONS,
  RdCategory,
  STATE_OPTIONS,
  useCurrentUserScopeForCurrentCompany,
  validateAmount,
} from '@neo.tax/shared';

import { useAccountingQreDetailsForm } from './hooks/useAccountingQreDetailsForm';
import { useAccountingQreDetailsFormAnalyticIds } from './hooks/useAccountingQreDetailsFormAnalyticIds';

type AccountingQreDetailsFormProps = {
  accountingQre: AccountingQre;
};

export const AccountingQreDetailsForm = ({ accountingQre }: AccountingQreDetailsFormProps): JSX.Element => {
  const { limitAccessToFinancialData } = useCurrentUserScopeForCurrentCompany();
  const { amountAnalyticId, countryAnalyticId, stateAnalyticId } = useAccountingQreDetailsFormAnalyticIds();

  const { control, watch } = useAccountingQreDetailsForm({ accountingQre });
  const countryCodeAlpha2 = watch('countryCodeAlpha2');

  const isEmployee = accountingQre.rdCategory === RdCategory.Employee;
  const isContractor = accountingQre.rdCategory === RdCategory.Contractor;
  const isCountryUnitedStates = countryCodeAlpha2 === 'US';

  if (limitAccessToFinancialData) {
    return <></>;
  }
  return (
    <Stack as="form" direction="column" paddingX={4} fullHeight>
      <ControlledInput
        label="Amount"
        name="amountDollars"
        rules={{
          required: 'Amount is required.',
          validate: validateAmount,
        }}
        id={amountAnalyticId}
        control={control}
        mask={InputMasks.NUMBER}
        maskReturnValueType="unmaskedValue"
        inlineLeadingContent="$"
        min={0}
      />
      <ControlledAutocompleteInput
        label="Country"
        name="countryCodeAlpha2"
        options={COUNTRY_OPTIONS}
        optionValueKey="codeAlpha2"
        optionDisplayKey="name"
        searchKeys={['name', 'codeAlpha2', 'codeAlpha3']}
        groupEndValues={['US']}
        rules={{
          required: {
            value: isEmployee || isContractor,
            message: 'Country is required.',
          },
        }}
        id={countryAnalyticId}
        control={control}
      />
      {isCountryUnitedStates && (
        <Indent>
          <ControlledAutocompleteInput
            label="State"
            name="stateCodeAlpha2"
            options={STATE_OPTIONS}
            searchKeys={['label', 'value']}
            rules={{
              required: {
                value: isEmployee || isContractor,
                message: 'State is required.',
              },
            }}
            id={stateAnalyticId}
            control={control}
          />
        </Indent>
      )}
    </Stack>
  );
};
